import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFilteredSites } from './trees/shared';
import { getActiveModuleKey } from './ModuleSwitcher';
import { updateUrlParams } from './utils';

/**
 * Hosts the URL-shape redirects that need to fire regardless of the sidebar's
 * presentation state (expanded / collapsed / Settings panel / Connect panel):
 *
 * 1. Monitor / Studio with no `spaceId` and no `buildingId` → first accessible
 *    floor under the first site, so the user lands on real data instead of
 *    an empty dashboard.
 * 2. Studio with `buildingId` but no `spaceId` → first accessible floor under
 *    that building, so the canvas has a floor to render.
 *
 * Previously these effects lived inside `SidebarMenu` and `StudioTree`, both
 * of which are only mounted in the expanded-sidebar branch — users who keep
 * the sidebar collapsed would never get redirected and would sit on an empty
 * page. Hoisting the effects here decouples them from the visual sidebar.
 */
// Acknowledged: this hook re-runs the access-filter pipeline that
// SidebarMenu also runs (Apollo cache writes hit both consumers). The
// redirector only needs the access-filtered tree to find the first
// floor — splitting `useFilteredSites` into a leaner access-only hook
// is a worthwhile follow-up but not required for correctness, since the
// filter only re-runs on actual structural changes (the `sitesFingerprint`
// gating already short-circuits on identical structure).
export function DefaultSpaceRedirector() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { sites } = useFilteredSites('');
  const module = getActiveModuleKey(pathname);

  // Default-floor redirect for Monitor / Studio when the URL carries no
  // scope params. `siteId` is intentionally NOT in the early-return guard
  // because Monitor / Studio don't read it (it's a Devices-only filter) —
  // leaving it in would short-circuit the redirect on stray `siteId` URLs
  // and land the user on an empty dashboard. `replace: true` keeps the
  // history clean so Back doesn't bounce to the empty URL.
  useEffect(() => {
    if (!sites || sites.length === 0) return;
    if (module === 'devices') return;

    const params = new URLSearchParams(search);
    if (params.has('spaceId') || params.has('buildingId')) return;

    for (const site of sites) {
      for (const building of site.buildings) {
        const firstFloor = building.floors[0];
        if (firstFloor) {
          params.set('spaceId', firstFloor.id);
          navigate({ search: `?${params.toString()}` }, { replace: true });
          return;
        }
      }
    }
  }, [sites, search, navigate, module]);

  // Studio-specific: when the URL has `?buildingId=…` but no `?spaceId=…`,
  // resolve to the first accessible floor under that building. The
  // `if (spaceId) return;` guard handles the bail cases naturally — once
  // we redirect, `updateUrlParams` writes `?spaceId=…` which clears
  // `buildingId` (mutually exclusive params), so the next dep-change sees
  // no buildingId and bails. Cache writes that arrive afterwards also see
  // `spaceId` set and bail. No "done ref" needed, and removing it fixes a
  // bug where browser-back to a different `?buildingId=…` would stay
  // stranded because the ref had latched true on the prior entry.
  useEffect(() => {
    if (module !== 'studio') return;
    if (!sites || sites.length === 0) return;
    const params = new URLSearchParams(search);
    const buildingId = params.get('buildingId');
    const spaceId = params.get('spaceId');
    if (!buildingId || spaceId) return;
    for (const site of sites) {
      const building = site.buildings.find((b) => b.id === buildingId);
      if (!building) continue;
      const firstFloor = building.floors[0];
      if (firstFloor) {
        updateUrlParams(navigate, { floorId: firstFloor.id });
      }
      return;
    }
  }, [module, sites, search, navigate]);

  return null;
}
